// src/services/staticJsonService.js
// R2 정적 JSON 오프로딩 서비스 - 성능 최적화

import { debugLog, errorLog, getEnvironment } from '../utils/environment';

class StaticJsonService {
  constructor() {
    this.baseUrl = 'https://static.painori.com';
    // 🔧 [수정] 환경별 fallback URL 분리
    this.getFallbackUrl = () => {
      const environment = getEnvironment();
      return environment === 'testnet' 
        ? 'https://test-api.painori.com'  // 테스트넷 전용
        : 'https://spot-api.painori.com'; // 메인넷용
    };
    this.cacheTimeout = 5 * 60 * 1000; // 5분 캐시
    this.memoryCache = new Map();
    this.cacheExpiry = new Map();
  }

  // 🚀 [최적화] 정적 JSON URL 생성
  getStaticUrl(endpoint) {
    const staticEndpoints = {
      'getDonationHallOfFame': 'hall-of-fame.json',
      // 🔴 [REMOVED 2026-01-29] 시즌 랭킹 JSON 제거
      'getRankingData': 'ranking-data.json'
    };
    
    return `${this.baseUrl}/${staticEndpoints[endpoint] || endpoint}.json`;
  }

  // 🚀 [최적화] 폴백 로직이 포함된 데이터 가져오기
  async fetchData(endpoint, params = {}) {
    const cacheKey = `${endpoint}-${JSON.stringify(params)}`;
    
    // 메모리 캐시 확인
    const cachedData = this.getCachedData(cacheKey);
    if (cachedData) {
      debugLog(`[StaticJson] ${endpoint} 메모리 캐시 히트`);
      return cachedData;
    }

    try {
      // 1차: R2 정적 JSON 시도
      const staticUrl = this.getStaticUrl(endpoint);
      debugLog(`[StaticJson] 정적 JSON 시도: ${staticUrl}`);
      
      const response = await fetch(staticUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: AbortSignal.timeout(3000) // 3초 timeout
      });

      if (response.ok) {
        const data = await response.json();
        this.setCachedData(cacheKey, data);
        debugLog(`[StaticJson] ✅ 정적 JSON 성공: ${endpoint}`);
        return data;
      }
    } catch (error) {
      debugLog(`[StaticJson] ❌ 정적 JSON 실패: ${error.message}`);
    }

    try {
      // 2차: Worker API 폴백
      debugLog(`[StaticJson] Worker API 폴백: ${endpoint}`);
      
      const response = await fetch(`${this.getFallbackUrl()}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
        signal: AbortSignal.timeout(10000) // 10초 timeout
      });

      if (response.ok) {
        const data = await response.json();
        this.setCachedData(cacheKey, data);
        debugLog(`[StaticJson] ✅ Worker API 성공: ${endpoint}`);
        return data;
      }
    } catch (error) {
      errorLog(`[StaticJson] ❌ Worker API 실패: ${error.message}`);
      throw error;
    }
  }

  // 🚀 [최적화] 캐시 관리
  getCachedData(cacheKey) {
    const expiry = this.cacheExpiry.get(cacheKey);
    if (expiry && Date.now() > expiry) {
      this.memoryCache.delete(cacheKey);
      this.cacheExpiry.delete(cacheKey);
      return null;
    }
    return this.memoryCache.get(cacheKey);
  }

  setCachedData(cacheKey, data) {
    this.memoryCache.set(cacheKey, data);
    this.cacheExpiry.set(cacheKey, Date.now() + this.cacheTimeout);
  }

  // 🚀 [최적화] 캐시 무효화
  invalidateCache(endpoint) {
    const keysToDelete = [];
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(endpoint)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.memoryCache.delete(key);
      this.cacheExpiry.delete(key);
    });
    
    debugLog(`[StaticJson] 캐시 무효화: ${endpoint} (${keysToDelete.length}개 항목)`);
  }

  // 🚀 [최적화] 배치 데이터 가져오기
  async fetchBatchData(endpoints) {
    const promises = endpoints.map(endpoint => 
      this.fetchData(endpoint).catch(error => {
        errorLog(`[StaticJson] 배치 요청 실패: ${endpoint}`, error);
        return null;
      })
    );

    const results = await Promise.all(promises);
    const data = {};
    
    endpoints.forEach((endpoint, index) => {
      if (results[index]) {
        data[endpoint] = results[index];
      }
    });

    return data;
  }
}

// 싱글톤 인스턴스 생성
const staticJsonService = new StaticJsonService();

export default staticJsonService;
